/**
 * Os filtros da aba Lançamentos e os atalhos de período.
 *
 * Papel: aqui ficam o que a barra de filtros guarda (`FiltrosDosLancamentos`),
 * os cinco atalhos de período da origem — "Hoje", as setas de dia, "Mês
 * Atual", as setas de mês e o "✕ Limpar" (l. 2740–2802) — e
 * `filtroDaConsulta`, que transforma o que está nos campos no recorte que
 * `GET /financas/lancamentos` aceita.
 *
 * Como usar: todo atalho recebe o "hoje" de quem chama, que é `hojeEmIso`, em
 * hora local; a origem usava `toISOString`, e de 21 h em diante andava um dia
 * (exceção 1). As contas de dia e de mês são feitas sobre o texto ISO em UTC,
 * sem `new Date('AAAA-MM-DD')` no fuso do navegador, que volta um dia em
 * Brasília.
 *
 * O campo vazio é texto vazio aqui e ausente no recorte: a URL não leva
 * filtro vazio, e a API lê o mês aberto só quando não há pesquisa nem período.
 */
import {
  type FiltroDeLancamentos,
  type FormaPagamentoLancamento,
  type TipoLancamento,
  listaTodosOsMeses,
} from '@concierge/shared';

/** O que os campos da barra de filtros guardam; vazio é "Todos". */
export interface FiltrosDosLancamentos {
  pesquisa: string;
  tipo: TipoLancamento | '';
  categoria: string;
  status: 'pago' | 'pendente' | '';
  forma: FormaPagamentoLancamento | '';
  /** O começo do período, `AAAA-MM-DD`, ou vazio. */
  de: string;
  /** O fim do período, `AAAA-MM-DD`, ou vazio. */
  ate: string;
}

/** Os campos como a aba abre: todos vazios. */
export const FILTROS_VAZIOS: FiltrosDosLancamentos = {
  pesquisa: '',
  tipo: '',
  categoria: '',
  status: '',
  forma: '',
  de: '',
  ate: '',
};

/** As duas pontas do período. */
export interface Periodo {
  de: string;
  ate: string;
}

/** Milissegundos de um dia. */
const UM_DIA = 86_400_000;

/**
 * Lê ano, mês (1–12) e dia de uma data ISO.
 *
 * @param iso - A data, `AAAA-MM-DD`.
 * @returns Os três números.
 */
function partes(iso: string): [number, number, number] {
  return [Number(iso.slice(0, 4)), Number(iso.slice(5, 7)), Number(iso.slice(8, 10))];
}

/**
 * Escreve uma data em UTC como ISO.
 *
 * @param instante - Milissegundos de `Date.UTC`.
 * @returns A data, `AAAA-MM-DD`.
 */
function iso(instante: number): string {
  return new Date(instante).toISOString().slice(0, 10);
}

/**
 * O primeiro e o último dia de um mês.
 *
 * @param ano - O ano.
 * @param mes - O mês, de 1 a 12; fora disso, vira o ano.
 * @returns O período do mês inteiro.
 */
function mesInteiro(ano: number, mes: number): Periodo {
  return { de: iso(Date.UTC(ano, mes - 1, 1)), ate: iso(Date.UTC(ano, mes, 0)) };
}

/**
 * O atalho "Hoje".
 *
 * @param hoje - A data de hoje, em hora local.
 * @returns "de" e "até" iguais a hoje.
 */
export function periodoDeHoje(hoje: string): Periodo {
  return { de: hoje, ate: hoje };
}

/**
 * As setas de dia: andam um dia a partir de "de", ou de hoje.
 *
 * @param de - O começo atual do período, ou vazio.
 * @param hoje - A data de hoje, em hora local.
 * @param dias - -1 pela seta ‹, +1 pela ›.
 * @returns "de" e "até" iguais ao dia de chegada.
 */
export function periodoDoDia(de: string, hoje: string, dias: number): Periodo {
  const [ano, mes, dia] = partes(de === '' ? hoje : de);
  const chegada = iso(Date.UTC(ano, mes - 1, dia) + dias * UM_DIA);
  return { de: chegada, ate: chegada };
}

/**
 * O atalho "Mês Atual".
 *
 * @param hoje - A data de hoje, em hora local.
 * @returns Do dia 1 ao último dia do mês de hoje.
 */
export function periodoDoMesAtual(hoje: string): Periodo {
  const [ano, mes] = partes(hoje);
  return mesInteiro(ano, mes);
}

/**
 * As setas de mês: andam um mês a partir do mês de "de", ou do de hoje.
 *
 * @param de - O começo atual do período, ou vazio.
 * @param hoje - A data de hoje, em hora local.
 * @param meses - -1 pela seta ‹, +1 pela ›.
 * @returns Do dia 1 ao último dia do mês de chegada.
 */
export function periodoDoMes(de: string, hoje: string, meses: number): Periodo {
  const [ano, mes] = partes(de === '' ? hoje : de);
  return mesInteiro(ano, mes + meses);
}

/**
 * Os campos depois do "✕ Limpar".
 *
 * @param hoje - A data de hoje, em hora local.
 * @returns Pesquisa, Tipo, Categoria, Status e Pagamento vazios; "de" no dia 1
 *   do mês de hoje e "até" em 31/12 do ano de hoje, como a origem.
 */
export function filtrosDoLimpar(hoje: string): FiltrosDosLancamentos {
  return { ...FILTROS_VAZIOS, de: `${hoje.slice(0, 7)}-01`, ate: `${hoje.slice(0, 4)}-12-31` };
}

/**
 * Transforma os campos no recorte da listagem.
 *
 * @param residenciaId - A casa do caixa.
 * @param mesAberto - O mês aberto da página, em `AAAA-MM`.
 * @param filtros - O que está nos campos.
 * @returns O recorte, sem os campos vazios; `competencia` só vai quando não há
 *   pesquisa nem período, que é quando a API a lê.
 */
export function filtroDaConsulta(
  residenciaId: string,
  mesAberto: string,
  filtros: FiltrosDosLancamentos,
): FiltroDeLancamentos {
  const pesquisa = filtros.pesquisa === '' ? undefined : filtros.pesquisa;
  const de = filtros.de === '' ? undefined : filtros.de;
  const ate = filtros.ate === '' ? undefined : filtros.ate;
  return {
    residenciaId,
    competencia: listaTodosOsMeses({ pesquisa, de, ate }) ? undefined : mesAberto,
    pesquisa,
    de,
    ate,
    tipo: filtros.tipo === '' ? undefined : filtros.tipo,
    categoria: filtros.categoria === '' ? undefined : filtros.categoria,
    status: filtros.status === '' ? undefined : filtros.status,
    forma: filtros.forma === '' ? undefined : filtros.forma,
  };
}
